import { FC, useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { PathKey, useNavigation } from '../../utils/urlPaths';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { QcsButton } from '../common/basic/QcsButton';
import { QcsTabs } from '../common/basic/QcsTabs';
import { QcsTypography } from '../common/basic/QcsTypography';
import { useAppDispatch, useAppSelector } from '../../store';
import { resetAnalytics } from '../../store/entities/analytics';
import { selectActiveTileScope, setActiveScope } from '../../store/entities/dashboardTiles';
import { selectIdentityRoles } from '../../store/entities/identity';
import { hasRole } from '../../utils/roles';
import { UserRole } from '@api/models/enums/UserRole';
import { TileScope } from '@api/models/dashboard/TileScope';
import { AnalyticsTypeCard } from './components';
import { ScopeSelector } from './components/ScopeSelector';
import { AnalyticsContainer, CollapseSection, CollapseSectionInner, ExplorerPane, OverviewPane, PageHeader, TypeCardsRow } from './AnalyticsStyles';
import { AnalyticsOverview } from './AnalyticsOverview';
import { AnalyticsExplorer } from './AnalyticsExplorer';
import { AnalyticsDomainConfig } from './analyticsDomainConfig';
import { ANALYTICS_DOMAINS, findDomainByKey } from './analyticsDomainRegistry';
import { useExplorerNavigation } from './useExplorerNavigation';

const BACK_BUTTON_SX = { flexShrink: 0 };
const PAGE_TITLE_SX = { whiteSpace: 'nowrap' };
const TABS_SX = { marginLeft: 'auto' };

export const Analytics: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { navigate } = useNavigation();
  const { domain: domainParam } = useParams<{ domain?: string }>();

  const identityRoles = useAppSelector(selectIdentityRoles);
  const isAdmin = hasRole(identityRoles, [UserRole.AdminQcs]);
  const activeScope = useAppSelector(selectActiveTileScope);

  const selectedDomain = useMemo(() => findDomainByKey(domainParam), [domainParam]);

  const handleScopeChange = useCallback(
    (scope: TileScope) => {
      dispatch(setActiveScope(scope));
    },
    [dispatch],
  );

  useLayoutEffect(() => {
    if (isAdmin && selectedDomain && activeScope === 'personal') {
      dispatch(setActiveScope('company'));
    }
  }, [isAdmin, selectedDomain, activeScope, dispatch]);

  const { activeTab, explorerConfig, handleExploreTile, handleTabChange, handleBackToOverview, handleStateChange, restoreSidebar } =
    useExplorerNavigation(selectedDomain);

  const [overviewMounted, setOverviewMounted] = useState(activeTab === 'overview');
  const [explorerMounted, setExplorerMounted] = useState(activeTab === 'explorer');
  if (activeTab === 'overview' && !overviewMounted) setOverviewMounted(true);
  if (activeTab === 'explorer' && !explorerMounted) setExplorerMounted(true);

  const handleCardClick = useCallback(
    (domain: AnalyticsDomainConfig) => {
      navigate(PathKey.AnalyticsDomain, { domain: domain.key });
    },
    [navigate],
  );

  const handleBackToIdle = useCallback(() => {
    dispatch(resetAnalytics());
    dispatch(setActiveScope(isAdmin ? 'company' : 'personal'));
    restoreSidebar();
    navigate(PathKey.Analytics);
  }, [dispatch, navigate, restoreSidebar, isAdmin]);

  return (
    <AnalyticsContainer>
      <PageHeader>
        {selectedDomain && (
          <QcsButton variant="outlined" onClick={handleBackToIdle} sx={BACK_BUTTON_SX}>
            <ArrowBackIcon />
          </QcsButton>
        )}
        <QcsTypography variant="h4" sx={PAGE_TITLE_SX}>
          {t(selectedDomain?.pageTitleKey ?? 'mainMenu.analytics')}
        </QcsTypography>

        {isAdmin && selectedDomain && <ScopeSelector value={activeScope} onChange={handleScopeChange} />}

        {selectedDomain && (
          <QcsTabs
            value={activeTab}
            onChange={handleTabChange}
            tabs={[
              { id: 'overview', label: t('analytics.tabs.overview') },
              { id: 'explorer', label: t('analytics.tabs.explorer') },
            ]}
            sx={TABS_SX}
          />
        )}
      </PageHeader>

      <CollapseSection open={!selectedDomain} aria-hidden={!!selectedDomain} inert={!!selectedDomain}>
        <CollapseSectionInner>
          <TypeCardsRow>
            {ANALYTICS_DOMAINS.map((domain) => (
              <AnalyticsTypeCard
                key={domain.key}
                icon={domain.card.icon}
                titleKey={domain.card.titleKey}
                descriptionKey={domain.card.descriptionKey}
                onClick={() => handleCardClick(domain)}
              />
            ))}
          </TypeCardsRow>
        </CollapseSectionInner>
      </CollapseSection>

      {selectedDomain && (
        <>
          <OverviewPane $active={activeTab === 'overview'}>{overviewMounted && <AnalyticsOverview onExploreTile={handleExploreTile} />}</OverviewPane>
          <ExplorerPane $active={activeTab === 'explorer'}>
            {explorerMounted && (
              <AnalyticsExplorer
                config={selectedDomain}
                initialConfig={explorerConfig}
                onBackToOverview={handleBackToOverview}
                onStateChange={handleStateChange}
              />
            )}
          </ExplorerPane>
        </>
      )}
    </AnalyticsContainer>
  );
};
